import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PlatformUser {
  id: string;
  full_name: string | null;
  username: string | null;
  profile_picture_url: string | null;
  belt_rank: string | null;
  academy_name: string | null;
}

/**
 * Fetches all active platform users for the admin new-chat picker.
 * Should only be called when the current user is an admin.
 */
export function useAllPlatformUsers() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['allPlatformUsers'],
    queryFn: async (): Promise<PlatformUser[]> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, username, profile_picture_url, belt_rank, main_academy:main_academy_id(name)')
        .neq('id', user!.id)
        .order('full_name', { ascending: true })
        .limit(500);

      if (error) throw error;

      return (data ?? []).map((p: any) => ({
        id:                  p.id,
        full_name:           p.full_name ?? null,
        username:            p.username ?? null,
        profile_picture_url: p.profile_picture_url ?? null,
        belt_rank:           p.belt_rank ?? null,
        academy_name:        p.main_academy?.name ?? null,
      }));
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  return { users: data ?? [], isLoading };
}
